import React from 'react';
import { Medal as MedalIcon } from 'lucide-react';
import { useParams } from 'react-router-dom';
import {
  useGetCourseDetailsQuery,
  useGetCourseTopicsQuery,
  useGetCourseMedalsQuery,
} from '../redux/apiSlice';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';

const AttemptCard = ({ courseId }: { courseId: number }) => {
  const {
    data: medalsData,
    isLoading: medalsLoading,
    error: medalsError,
  } = useGetCourseMedalsQuery(courseId);
  const formatTimeFromMinutes = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return hours > 0
      ? `${hours} hours ${remainingMinutes} minutes - Completed`
      : `${remainingMinutes} minutes - Completed`;
  };

  const renderMedalIcon = (medal: string, size = 24) => {
    switch (medal) {
      case 'gold':
        return <MedalIcon color="gold" size={size} />;
      case 'silver':
        return <MedalIcon color="silver" size={size} />;
      case 'bronze':
      default:
        return <MedalIcon color="#cd7f32" size={size} />;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-semibold mb-4 text-gray-800 border-b pb-2">
        Course Completion History
      </h2>
      <div className="space-y-4 flex flex-col-reverse gap-4">
        {medalsData && medalsData.length > 0 ? (
          medalsData.map((attempt, index) => (
            <div
              key={index}
              className="flex items-center justify-between bg-gray-50 p-4 rounded-md"
            >
              <div>
                <h3 className="text-lg font-medium">Attempt {index + 1}</h3>
                <p className="text-base text-gray-600">
                  {formatTimeFromMinutes(attempt.completed_in)}
                </p>
                {/* {attempt.feedback && (
                  <p className="text-sm text-gray-500 mt-1">
                    {attempt.feedback}
                  </p>
                )} */}
              </div>
              <div className="flex items-center bg-white p-2 rounded-full shadow-sm">
                {renderMedalIcon(attempt.medal, 32)}
                <span className="ml-2 text-base font-medium capitalize">
                  {attempt.medal}
                </span>
              </div>
            </div>
          ))
        ) : (
          <p className="text-center text-gray-600">
            No completion history available
          </p>
        )}
      </div>
    </div>
  );
};

const CourseDetails: React.FC = () => {
  const { courseId: id } = useParams<{ courseId: string }>();
  const courseId = id ? parseInt(id) : 1; // Default to 1 if no ID provided

  // Get authentication state from Redux store
  const isAuthenticated = useSelector(
    (state: RootState) => state.app.user.isAuthenticated,
  );

  const {
    data: courseData,
    isLoading: courseLoading,
    error: courseError,
  } = useGetCourseDetailsQuery(courseId);

  const {
    data: topicsData,
    isLoading: topicsLoading,
    error: topicsError,
  } = useGetCourseTopicsQuery(courseId);

  const formatTimeFromMinutes = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return hours > 0
      ? `${hours} hours ${remainingMinutes} minutes - Completed`
      : `${remainingMinutes} minutes - Completed`;
  };

  if (courseLoading || topicsLoading) {
    return (
      <div className="container mx-auto p-8 text-center">
        Loading course details...
      </div>
    );
  }

  if (courseError || topicsError) {
    return (
      <div className="container mx-auto p-8 text-center text-red-500">
        Error loading course details
      </div>
    );
  }

  // If the course data array is empty, show a not found message
  if (!courseData) {
    return (
      <div className="container mx-auto p-8 text-center">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">
          Course Not Found
        </h1>
        <p className="text-gray-600">
          The requested course could not be found. Please check the course ID
          and try again.
        </p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl text-lg">
      {/* Header with course name - Card format */}
      <div className="mb-8 bg-white rounded-lg shadow-md p-6">
        <h1 className="text-4xl font-bold text-gray-800 mb-4 text-center">
          {courseData.course_name}
        </h1>
        <div className="flex justify-center gap-4 flex-wrap">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-base font-medium bg-blue-100 text-blue-800">
            Duration: {courseData.duration} Hours
          </span>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-base font-medium bg-green-100 text-green-800">
            Level:{' '}
            {courseData.level?.name.charAt(0).toUpperCase() +
              courseData.level?.name.slice(1).toLowerCase()}
          </span>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-base font-medium bg-purple-100 text-purple-800">
            Language:{' '}
            {courseData.language?.name.toLowerCase() === 'html/css'
              ? 'Html/Css'
              : courseData.language?.name.charAt(0).toUpperCase() +
                courseData.language?.name.slice(1).toLowerCase()}
          </span>
        </div>
      </div>

      {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"> */}
      {/* Course Objective with increased width (now spans 3 columns) */}
      <div className="md:col-span-3">
        <div className="bg-white rounded-lg shadow-md p-6 h-full">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800 border-b pb-2">
            Course Objective
          </h2>
          <p
            dangerouslySetInnerHTML={{ __html: courseData.objective }}
            className="text-lg text-gray-700 leading-relaxed"
          />
        </div>
      </div>

      {/* Right column: Time Spent */}
      {/* <div className="md:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-6 h-full">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800 border-b pb-2">
              Total Time Spent
            </h2>
            <div className="flex items-center justify-center h-full">
              <p className="text-3xl font-bold text-indigo-600">
                13 Hours 57 minutes
              </p>
            </div>
          </div>
        </div> */}
      {/* </div> */}

      {/* Topics Covered */}
      <div className="bg-white rounded-lg shadow-md p-6 my-8">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800 border-b pb-2">
          Topics Covered
        </h2>
        {topicsData && topicsData.length > 0 ? (
          <ul className="space-y-3">
            {topicsData.map((topic, index) => (
              <li key={topic.id} className="bg-gray-50 p-4 rounded-md">
                <h3 className="text-lg font-medium text-indigo-700">
                  Section {index + 1}: {topic.title}{' '}
                </h3>
                <p
                  dangerouslySetInnerHTML={{ __html: topic.description }}
                  className="text-base text-gray-600 mt-1 output-area"
                />
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-lg text-gray-600 text-center py-4">
            No topics available for this course.
          </p>
        )}
      </div>

      {/* History of Attempts - Only shown if user is logged in */}
      {isAuthenticated && <AttemptCard courseId={courseId} />}
    </div>
  );
};

export default CourseDetails;
